/**
 * MMX Social Wall — the auto-scrolling row of "Social post" cards.
 *
 * Moved out of MMX Symon Says. The wall duplicates its own track so the marquee can loop
 * seamlessly without the section having to render the blocks twice, and plays card videos in
 * place (muted autoplay while on screen, with a per-card sound toggle). The cards are the
 * unchanged `mmx-symon-card` block, hence the `data-symon-*` hooks.
 */

class MmxSocialWall extends HTMLElement {
  connectedCallback() {
    this.#setupMarquee();
    this.#setupVideos();
  }

  /* ---------------------------------- Marquee ---------------------------------- */

  #setupMarquee() {
    const track = this.querySelector('[data-symon-track]');
    const wall = track?.parentElement;
    if (!track || !wall) return;

    const cards = Array.from(track.children);
    if (cards.length === 0) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const setWidth = track.scrollWidth;

    // The artboard shows the row overhanging both edges evenly. Auto margins collapse to 0
    // once the track overflows, so the start position is set here instead — before cloning,
    // so the offset is measured against a single copy of the set.
    wall.scrollLeft = Math.max(0, (setWidth - wall.clientWidth) / 2);

    if (this.dataset.marquee !== 'true') return;

    // Duplicate the set until the track can cover the viewport plus a full loop, so the
    // -(setWidth + gap) keyframe always lands on an identical frame.
    const copies = Math.max(2, Math.ceil((window.innerWidth + setWidth) / Math.max(setWidth, 1)) + 1);

    for (let copy = 1; copy < copies; copy += 1) {
      cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        track.appendChild(clone);
      });
    }

    // One loop travels exactly one copy of the set, so speed stays constant whatever the count.
    const speed = Math.max(Number(this.dataset.speed) || 40, 1);
    track.style.setProperty('--mmx-wall-marquee-shift', `${-(setWidth + gap)}px`);
    track.style.setProperty('--mmx-wall-marquee-duration', `${(setWidth + gap) / speed}s`);
    track.classList.add('is-animated');
  }

  /* ---------------------------------- Videos ---------------------------------- */

  /**
   * Uploaded videos autoplay muted and looped while their card is on screen, and pause once it
   * leaves (the marquee's clones are real videos too, so only the visible few ever download).
   * A click on the card toggles play / pause; the corner button toggles sound, and only one
   * card is heard at a time. The marquee holds still while a card is playing with sound.
   *
   * Cards are cloned by the marquee, so everything is delegated from the wall rather than
   * wired per card. An external video is embedded on first click only.
   */
  #setupVideos() {
    this.track = this.querySelector('[data-symon-track]');
    this.videos = Array.from(this.querySelectorAll('[data-symon-video]'));
    this.onScreen = new WeakSet();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = Boolean(navigator.connection?.saveData);
    this.autoplay = !reducedMotion && !saveData;

    this.videos.forEach((video) => {
      // The attribute alone is not reliably reflected on clones or in every browser.
      video.muted = true;
      const card = video.closest('.mmx-symon__card');
      card?.classList.add('mmx-symon__card--autoplay');
      if (!this.autoplay) card?.classList.add('is-paused');
    });

    // Observed even without autoplay, so a video the visitor started still pauses off screen.
    if (this.videos.length > 0) {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((entry) => this.#onVisibility(entry.target, entry.isIntersecting)),
          { threshold: 0.25 }
        );
        this.videos.forEach((video) => this.observer.observe(video));
      } else {
        this.videos.forEach((video) => this.#onVisibility(video, true));
      }
    }

    // Media events do not bubble, so state is synced from a capturing listener.
    this.onMediaEvent = (event) => {
      if (event.target instanceof HTMLVideoElement) this.#sync(event.target);
    };
    ['play', 'pause', 'volumechange'].forEach((type) => this.addEventListener(type, this.onMediaEvent, true));

    // Nothing keeps playing — or talking — in a background tab.
    this.onPageVisibility = () => {
      this.videos.forEach((video) => {
        if (document.hidden) {
          video.pause();
        } else if (this.onScreen.has(video) && this.#wantsToPlay(video)) {
          this.#play(video);
        }
      });
    };
    document.addEventListener('visibilitychange', this.onPageVisibility);

    this.addEventListener('click', (event) => {
      const card = event.target.closest('.mmx-symon__card--video');
      if (!card || !this.contains(card)) return;

      event.preventDefault();
      const video = card.querySelector('[data-symon-video]');

      if (!video) {
        this.#playEmbed(card);
      } else if (event.target.closest('[data-symon-sound]')) {
        this.#toggleSound(card, video);
      } else {
        this.#togglePlay(card, video);
      }
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onPageVisibility) document.removeEventListener('visibilitychange', this.onPageVisibility);
  }

  #wantsToPlay(video) {
    // Without autoplay every card starts (and returns from off screen) as `is-paused`.
    return !video.closest('.mmx-symon__card')?.classList.contains('is-paused');
  }

  #onVisibility(video, visible) {
    if (visible) {
      this.onScreen.add(video);
      if (this.#wantsToPlay(video) && !document.hidden) this.#play(video);
      return;
    }

    this.onScreen.delete(video);
    video.pause();
    // Without autoplay nothing restarts it on return, so it comes back showing the play button.
    if (!this.autoplay) video.closest('.mmx-symon__card')?.classList.add('is-paused');
    // A card that scrolls away goes quiet, so it never comes back talking.
    video.muted = true;
  }

  #play(video) {
    const played = video.play();
    if (!played || typeof played.catch !== 'function') return;

    played.catch((error) => {
      // AbortError just means a pause() overtook the play() — e.g. the card scrolled away.
      if (error?.name === 'AbortError') return;
      // Autoplay refused (iOS Low Power Mode, browser policy) or no playable source:
      // fall back to the poster with a play button.
      video.closest('.mmx-symon__card')?.classList.add('is-paused');
      this.#sync(video);
    });
  }

  #togglePlay(card, video) {
    if (video.paused) {
      card.classList.remove('is-paused');
      this.#play(video);
    } else {
      card.classList.add('is-paused');
      video.pause();
    }
  }

  #toggleSound(card, video) {
    if (!video.muted) {
      video.muted = true;
      return;
    }

    this.#muteAll();
    video.muted = false;

    // Asking for sound on a paused card means "play this".
    if (video.paused) {
      card.classList.remove('is-paused');
      this.#play(video);
    }
  }

  #muteAll() {
    this.videos.forEach((video) => {
      video.muted = true;
    });
  }

  #sync(video) {
    const card = video.closest('.mmx-symon__card');
    if (card) {
      const playing = !video.paused;
      card.classList.toggle('is-playing', playing);
      card.classList.toggle('is-unmuted', !video.muted);
      card.querySelector('[data-symon-sound]')?.setAttribute('aria-pressed', String(!video.muted));
      card.querySelector('[data-symon-play]')?.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    const audible = this.videos.some((v) => !v.paused && !v.muted);
    this.#pauseMarquee(audible || Boolean(this.querySelector('.mmx-symon__card.is-embedded')));
  }

  #playEmbed(card) {
    const src = card.dataset.symonEmbed;
    if (!src || card.classList.contains('is-embedded')) return;

    const iframe = document.createElement('iframe');
    iframe.className = 'mmx-symon__card-embed';
    iframe.src = src;
    iframe.title = 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.setAttribute('frameborder', '0');

    // The embed plays with sound, so the wall's own videos step aside.
    this.#muteAll();
    card.querySelector('.mmx-symon__card-image')?.remove();
    card.appendChild(iframe);
    card.classList.add('is-embedded');
    this.#pauseMarquee(true);
  }

  #pauseMarquee(paused) {
    this.track?.classList.toggle('is-paused', paused);
  }
}

if (!customElements.get('mmx-social-wall')) {
  customElements.define('mmx-social-wall', MmxSocialWall);
}
